// React & Hooks
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { formatCurrency, transformDateToLocalDateString } from "helpers";

// API & Types
import { DataWithPagination } from "services/api";
import { getFleetVehicleOfferSalesAPI } from "services/api/fleet/vehicles/offers/api";
import { FleetVehicleOfferSaleRow } from "services/api/fleet/vehicles/offers/types";
import { initialDataWithPagination } from "services/api/consts";
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// Component types
import { FleetOfferSaleDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";

// UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";

// Redak tooltipa ponude.
const OfferTooltipRow: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex justify-between text-sm mb-1">
    <span className="text-slate-300">{label}</span>
    <span className={strong ? "font-bold text-green-400" : "font-medium text-right max-w-[180px] truncate"}>{value}</span>
  </div>
);

// Bogati tooltip ponude — prikaz na hover odabrane vrijednosti.
const OfferTooltip: React.FC<{ offer: FleetVehicleOfferSaleRow }> = ({ offer }) => {
  const clientName = offer.contactClient?.name || offer.contactClient?.companyName || "—";
  return (
    <div className="min-w-[260px]">
      <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji ponude</div>
      <OfferTooltipRow label="Broj" value={offer.offerNumber || "—"} />
      <OfferTooltipRow label="Kupac" value={clientName} />
      {offer.status?.name && <OfferTooltipRow label="Status" value={offer.status.name} />}
      {offer.offerDate && <OfferTooltipRow label="Datum ponude" value={transformDateToLocalDateString(offer.offerDate)} />}
      {offer.dueDate && <OfferTooltipRow label="Vrijedi do" value={transformDateToLocalDateString(offer.dueDate)} />}
      {offer.durationDays != null && <OfferTooltipRow label="Trajanje" value={`${offer.durationDays} dana`} />}
      {((offer.total ?? 0) > 0 || (offer.totalWithoutTax ?? 0) > 0) && (
        <>
          <div className="border-t border-slate-600 my-2" />
          {(offer.totalWithoutTax ?? 0) > 0 && <OfferTooltipRow label="Bez PDV-a" value={formatCurrency.format(offer.totalWithoutTax!)} />}
          {(offer.totalDiscount ?? 0) > 0 && <OfferTooltipRow label="Popust" value={formatCurrency.format(offer.totalDiscount!)} />}
          {(offer.total ?? 0) > 0 && <OfferTooltipRow label="Ukupno" value={formatCurrency.format(offer.total!)} strong />}
        </>
      )}
    </div>
  );
};

const FleetOfferSaleDropdown: React.FC<FleetOfferSaleDropdownProps> = (props) => {
  const {
    name,
    vehicleId,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    onOfferSelect,
    showLinkedToContract,
    showCancelled,
    autoSelectLast,
    onLoaded,
  } = props;

  const [searchValue, setSearchValue] = useState<string>("");
  const autoSelectedRef = useRef<boolean>(false);
  const pagination = initialDataWithPagination.pagination;

  const cacheKey = buildDropdownCacheKey(DropdownCacheKey.FleetOfferSales, vehicleId, showLinkedToContract, showCancelled, searchValue);

  const { data, isLoading } = useDropdownCache<DataWithPagination<FleetVehicleOfferSaleRow>>(
    cacheKey,
    () => getFleetVehicleOfferSalesAPI(
      pagination,
      {
        vehicleId,
        showLinkedToContract,
        showCancelled,
        search: searchValue || undefined,
        showItems: false,
        showVehicle: false,
        showStatus: false,
        showPdfUpload: false,
      },
    ),
    5 * 60 * 1000
  );

  const searchResults = data ?? initialDataWithPagination;

  // Reset auto-select when vehicle changes
  useEffect(() => {
    autoSelectedRef.current = false;
  }, [vehicleId]);

  // Notify caller when data arrives
  useEffect(() => {
    if (data && onLoaded) onLoaded(data.data.length);
  }, [data]);

  const handleSearchChange = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setSearchValue("");
  }, []);

  // Auto-select last offer
  useEffect(() => {
    if (!autoSelectLast || autoSelectedRef.current || isLoading || value) return;
    if (searchResults.data.length === 0) return;

    const lastOffer = searchResults.data[0];
    autoSelectedRef.current = true;

    const syntheticItem: DropdownOption = {
      value: lastOffer.id,
      text: lastOffer.offerNumber || "",
    };
    const syntheticEvent = { target: { dataset: {} } };
    onChange(syntheticEvent, name, syntheticItem);
    if (onOfferSelect) onOfferSelect(lastOffer);
  }, [searchResults.data, isLoading, autoSelectLast, value]);

  const offerItems: DropdownOption[] = useMemo(() => searchResults.data.map((i) => {
    const clientName = i.contactClient?.name || i.contactClient?.companyName || "";
    return {
      value: i.id,
      text: (<div className="flex items-center justify-between w-full">
        <span>{i.offerNumber} </span>
        <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">{clientName}</span>
      </div>),
    };
  }), [searchResults.data]);

  const handleChange = (e: any, fieldName: string, item: DropdownOption) => {
    onChange(e, fieldName, item);

    if (onOfferSelect) {
      const selectedOffer = searchResults.data.find((offer) => offer.id === item.value) || null;
      onOfferSelect(selectedOffer);
    }
  };

  // Odabrana ponuda — za tooltip na hover.
  const selectedOffer = useMemo(
    () => searchResults.data.find((offer) => offer.id === value),
    [searchResults.data, value]
  );

  return (
    <DropdownAutocomplete
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      value={value}
      direction={direction}
      width={width}
      height={height}
      options={offerItems}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      error={error}
      isLoading={isLoading}
      tooltip={selectedOffer ? <OfferTooltip offer={selectedOffer} /> : undefined}
    />
  );
};

export default React.memo(FleetOfferSaleDropdown);
